import { useParams } from 'react-router-dom';
import Accordion from '../../../../components/Accordion/Accordion';
import { AccordionSummaryContentBox } from '../../../../components/Survey/Survey';
import React, { useState } from 'react';
import { ICustomSurveyQuestion } from '../../../../shared/interfaces/ICustomSurveyQuestion';
import { QuestionTypes } from '../../../../shared/interfaces/ISurvey';
import { useTranslation } from 'react-i18next';
import Chip from '../../../../components/Chip/Chip';
import { Button, Input, TextField, Typography, useTheme } from '@mui/material';
import { LANGUAGES } from '../languages';
import TranslationsService from '../../../../services/TranslationsService';
import SurveyUtils from '../../../../shared/SurveyUtils';

interface SurveyQuestionLocalizationProps {
  question: ICustomSurveyQuestion;
  currentLanguage?: keyof typeof LANGUAGES | string;
  setQuestionLocalization: (id: string | undefined, val: string) => void;
  setOptionLocalization: (id: string | undefined, idx: number, val: string) => void;
  setRowLocalization: (id: string | undefined, idx: number, val: string) => void;
  setPlaceholderLocalization: (id: string | undefined, val: string) => void;
  setPrivacyNoticeTextLocalization: (id: string | undefined, val: string) => void;
  onSave: (id: string | undefined) => void;
}

const SurveyQuestionLocalization: React.FC<SurveyQuestionLocalizationProps> = ({
  question,
  currentLanguage,
  setQuestionLocalization,
  setOptionLocalization,
  setRowLocalization,
  setPlaceholderLocalization,
  setPrivacyNoticeTextLocalization,
  onSave,
}) => {
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();

  const autoTranslateQuestion = async () => {
    if (!currentLanguage) return;

    const controller = new AbortController();
    const targetLanguage = currentLanguage.slice(0, 2);
    const res = await TranslationsService.translate(
      question.question,
      targetLanguage,
      setLoading,
      controller.signal
    );

    if (res) {
      setQuestionLocalization(question.questionId, res);
    }
  };

  const autoTranslatePlaceholder = async () => {
    if (!currentLanguage || !question.placeholder) return;

    const controller = new AbortController();
    const targetLanguage = currentLanguage.slice(0, 2);
    const res = await TranslationsService.translate(
      question.placeholder,
      targetLanguage,
      setLoading,
      controller.signal
    );

    if (res) {
      setPlaceholderLocalization(question.questionId, res);
    }
  };

  const autoTranslatePrivacyNoticeText = async () => {
    if (!currentLanguage || !question.PrivacyNoticeText) return;

    const controller = new AbortController();
    const targetLanguage = currentLanguage.slice(0, 2);
    const res = await TranslationsService.translate(
      question.PrivacyNoticeText,
      targetLanguage,
      setLoading,
      controller.signal
    );

    if (res) {
      setPrivacyNoticeTextLocalization(question.questionId, res);
    }
  };

  const autoTranslateOption = async (idx: number) => {
    if (!currentLanguage) return;

    SurveyUtils.addDefaultNpsOptions(question, t);

    const controller = new AbortController();
    const targetLanguage = currentLanguage.slice(0, 2);
    const res = await TranslationsService.translate(
      question.options[idx].label,
      targetLanguage,
      setLoading,
      controller.signal
    );

    if (res) {
      setOptionLocalization(question.questionId, idx, res);
    }
  };

  const autoTranslateRow = async (idx: number) => {
    if (!currentLanguage || !question.rows?.[idx]) return;

    const controller = new AbortController();
    const targetLanguage = currentLanguage.slice(0, 2);
    const res = await TranslationsService.translate(
      question.rows[idx].label,
      targetLanguage,
      setLoading,
      controller.signal
    );

    if (res) {
      setRowLocalization(question.questionId, idx, res);
    }
  };
  const getQuestionTypeName = (type: QuestionTypes) => {
    switch (type) {
      case 'CUSTOMSINGLE':
        return t('survey_question_customsingle');
      case 'CUSTOMMULTIPLE':
        return t('survey_question_custommultiple');
      case 'CUSTOMTEXT':
        return t('survey_question_customtext');
      case 'CUSTOMYESNO':
        return t('survey_question_customyesno');
      case 'CUSTOMSCALENPS':
        return t('survey_question_customscalenps');
      case 'CUSTOMDROPDOWN':
        return t('survey_question_customdropdown');
      case 'CUSTOMGATE':
        return t('survey_question_customgate');
      case 'CUSTOMFEEDBACK':
        return t('survey_question_feedback');
      case 'CUSTOMMATRIX':
        return t('survey_question_custommatrix');
      default:
        return type;
    }
  };

  const getCurrentTranslation = () =>
    currentLanguage &&
    question.questionI18n &&
    question.questionI18n[currentLanguage as keyof typeof LANGUAGES]
      ? question.questionI18n[currentLanguage as keyof typeof LANGUAGES]
      : '';

  const translateOption = (optionIdx: number) =>
    currentLanguage &&
    question.questionI18n &&
    question.questionI18n.options &&
    question.questionI18n.options[currentLanguage]
      ? question.questionI18n.options[currentLanguage as keyof typeof LANGUAGES][optionIdx] ?? ''
      : '';

  const translateRow = (rowIdx: number) =>
    currentLanguage &&
    question.questionI18n &&
    question.questionI18n.rows &&
    question.questionI18n.rows[currentLanguage]
      ? question.questionI18n.rows[currentLanguage as keyof typeof LANGUAGES][rowIdx] ?? ''
      : '';

  const translatePlaceholder = () =>
    currentLanguage &&
    question.questionI18n &&
    question.questionI18n.placeholder &&
    question.questionI18n.placeholder[currentLanguage]
      ? question.questionI18n.placeholder[currentLanguage as keyof typeof LANGUAGES] ?? ''
      : '';

  const translatePrivacyNoticeText = () =>
    currentLanguage &&
    question.questionI18n &&
    question.questionI18n.PrivacyNoticeText &&
    question.questionI18n.PrivacyNoticeText[currentLanguage]
      ? question.questionI18n.PrivacyNoticeText[currentLanguage as keyof typeof LANGUAGES] ?? ''
      : '';

  return (
    <>
      <Accordion
        summary={
          <AccordionSummaryContentBox>
            {question.question}

            <Chip
              size="small"
              label={getQuestionTypeName(question.type) || question.type}
              sx={{ ml: theme.spacing(2) }}
            />
          </AccordionSummaryContentBox>
        }
        details={
          <>
            <div
              style={{
                display: 'grid',
                width: '50%',
                gap: '1rem',
                gridTemplateColumns: '45% 5rem 1fr',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography sx={{ textAlign: 'right' }}>{question.question}</Typography>
              <Button
                variant={'contained'}
                onClick={() => autoTranslateQuestion()}
              >
                &gt;
              </Button>
              <TextField
                sx={{ flexGrow: 1 }}
                disabled={!currentLanguage}
                value={getCurrentTranslation()}
                label={t('question_text')}
                multiline={true}
                onChange={(ev) => {
                  setQuestionLocalization(question.questionId, ev.target.value);
                }}
              />
            </div>
            {(question.placeholder ||
              question.type === 'CUSTOMTEXT' ||
              question.type === 'CUSTOMFEEDBACK') && (
              <div>
                <h3>{t('question_placeholder')}</h3>
                <div
                  style={{
                    width: '50%',
                    display: 'grid',
                    gap: '1rem',
                    gridTemplateColumns: '45% 5rem 1fr',
                    gridTemplateRows: 'auto',
                    marginBottom: '1rem',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  {' '}
                  <Typography sx={{ textAlign: 'right' }}>{question.placeholder}</Typography>
                  <Button
                    variant={'contained'}
                    onClick={() => autoTranslatePlaceholder()}
                  >
                    &gt;
                  </Button>
                  <TextField
                    disabled={!currentLanguage}
                    sx={{ flexGrow: 1 }}
                    value={translatePlaceholder()}
                    label={t('label')}
                    multiline={true}
                    onChange={(ev) => {
                      setPlaceholderLocalization(question.questionId, ev.target.value);
                    }}
                  ></TextField>
                </div>
                {question.ShouldShowPrivacyNotice && (
                  <>
                    <h3>{t('survey_privacy_notice')}</h3>
                    <div
                      style={{
                        width: '50%',
                        display: 'grid',
                        gap: '1rem',
                        gridTemplateColumns: '45% 5rem 1fr',
                        gridTemplateRows: 'auto',
                        marginBottom: '1rem',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      {' '}
                      <Typography sx={{ textAlign: 'right' }}>
                        {question.PrivacyNoticeText}
                      </Typography>
                      <Button
                        variant={'contained'}
                        onClick={() => autoTranslatePrivacyNoticeText()}
                      >
                        &gt;
                      </Button>
                      <TextField
                        disabled={!currentLanguage}
                        sx={{ flexGrow: 1 }}
                        multiline={true}
                        value={translatePrivacyNoticeText()}
                        label={t('survey_privacy_notice')}
                        onChange={(ev) => {
                          setPrivacyNoticeTextLocalization(question.questionId, ev.target.value);
                        }}
                      ></TextField>
                    </div>
                  </>
                )}
              </div>
            )}
            {question.type === 'CUSTOMMATRIX' && (question.rows?.length ?? 0) > 0 && (
              <div>
                <h3>Aussagen:</h3>
                <div
                  style={{
                    width: '50%',
                    display: 'grid',
                    gap: '1rem',
                    gridTemplateColumns: '45% 5rem 1fr',
                    gridTemplateRows: 'auto',
                    marginBottom: '1rem',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  {(question.rows ?? []).map((row, idx) => (
                    <>
                      <Typography sx={{ textAlign: 'right' }}>
                        Aussage {idx + 1}: {row.label}
                      </Typography>
                      <Button
                        onClick={() => autoTranslateRow(idx)}
                        variant={'contained'}
                      >
                        &gt;
                      </Button>
                      <TextField
                        disabled={!currentLanguage}
                        sx={{ flexGrow: 1 }}
                        value={translateRow(idx)}
                        label={t('label')}
                        multiline={true}
                        onChange={(ev) => {
                          setRowLocalization(question.questionId, idx, ev.target.value);
                        }}
                      ></TextField>
                    </>
                  ))}
                </div>
              </div>
            )}
            {question.type !== 'CUSTOMGATE' &&
              ((question.options && question.options.length > 0) ||
                question.type === 'CUSTOMSCALENPS') && (
                <div>
                  <h3>Optionen:</h3>
                  <div
                    style={{
                      width: '50%',
                      display: 'grid',
                      gap: '1rem',
                      gridTemplateColumns: '45% 5rem 1fr',
                      gridTemplateRows: 'auto',
                      marginBottom: '1rem',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    {question.options.length
                      ? question.options.map((o, idx) => (
                          <>
                            <Typography sx={{ textAlign: 'right' }}>
                              Option {idx + 1}: {o.label}
                            </Typography>
                            <Button
                              onClick={() => autoTranslateOption(idx)}
                              variant={'contained'}
                            >
                              &gt;
                            </Button>
                            <TextField
                              disabled={!currentLanguage}
                              sx={{ flexGrow: 1 }}
                              value={translateOption(idx)}
                              label={t('label')}
                              multiline={true}
                              onChange={(ev) => {
                                setOptionLocalization(question.questionId, idx, ev.target.value);
                              }}
                            ></TextField>
                          </>
                        ))
                      : question.type === 'CUSTOMSCALENPS' &&
                        [{ label: t('nps_low') }, { label: t('nps_high') }].map((o, idx) => (
                          <>
                            <Typography sx={{ textAlign: 'right' }}>
                              Option {idx + 1}: {o.label}
                            </Typography>
                            <Button
                              onClick={() => autoTranslateOption(idx)}
                              variant={'contained'}
                            >
                              &gt;
                            </Button>
                            <TextField
                              disabled={!currentLanguage}
                              sx={{ flexGrow: 1 }}
                              multiline={true}
                              value={translateOption(idx)}
                              label={t('label')}
                              onChange={(ev) => {
                                setOptionLocalization(question.questionId, idx, ev.target.value);
                              }}
                            ></TextField>
                          </>
                        ))}
                  </div>
                </div>
              )}

            <Button
              onClick={() => {
                onSave(question.questionId);
                setIsExpanded(false);
              }}
              variant={'contained'}
            >
              Speichern
            </Button>
          </>
        }
        expanded={isExpanded}
        onExpandRequest={() => setIsExpanded(!isExpanded)}
      />
    </>
  );
};

export default SurveyQuestionLocalization;
